import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import type { PreviewYamlProps } from '@cast/design-system';
import { Box, tokens, withAlpha } from '@cast/design-system';
import { useCallbackWhenDomChanges } from '@cast/utils';

import { CopyToClipboard } from 'components/clickable-actions/CopyToClipboard';
import { PreviewYaml } from 'components/code';

import {
  applyYamlHighlights,
  clearYamlHighlights,
  findYamlScroller,
  getHighlightedYamlText,
  getLineNumberFromEvent,
} from '../_utils/yamlLine';
import type { LineClickHandler } from '../types';

const DRAWER_ANIMATION_DELAY = 300; // MUI Drawer transition duration is 225ms, add buffer for safety

const highlightAndScroll = ({
  container,
  defaultHighlighted,
  activeHighlighted,
  scrollTimeoutRef,
}: {
  container: HTMLElement;
  defaultHighlighted: number[];
  activeHighlighted: number[];
  scrollTimeoutRef: React.RefObject<number | null>;
}) => {
  const hasHighlights =
    defaultHighlighted.length > 0 || activeHighlighted.length > 0;

  if (!hasHighlights) {
    clearYamlHighlights(container);
    return;
  }

  const firstHighlighted = applyYamlHighlights(
    container,
    defaultHighlighted,
    activeHighlighted
  );
  if (!firstHighlighted) {
    return;
  }

  if (scrollTimeoutRef.current !== null) {
    window.clearTimeout(scrollTimeoutRef.current);
  }
  scrollTimeoutRef.current = window.setTimeout(() => {
    scrollTimeoutRef.current = null;
    if (!firstHighlighted.isConnected) {
      return;
    }
    const scroller = findYamlScroller(container);
    if (!scroller.isConnected) return;

    scroller.scrollLeft = 0;
    firstHighlighted.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'start',
    });
  }, DRAWER_ANIMATION_DELAY);
};

type YamlLineWithHighlightProps = PreviewYamlProps & {
  defaultHighlightedLineNumbers?: number[];
  activeHighlightedLineNumbers?: number[];
  manifestId?: string;
  onLineClick?: LineClickHandler;
  children?: ReactNode;
};

export const YamlLineWithHighlight = ({
  defaultHighlightedLineNumbers = [],
  activeHighlightedLineNumbers = [],
  manifestId,
  onLineClick,
  children,
  ...props
}: YamlLineWithHighlightProps) => {
  const [containerElement, setContainerElement] =
    useState<HTMLDivElement | null>(null);
  const [highlightedText, setHighlightedText] = useState('');
  const hasHighlights =
    defaultHighlightedLineNumbers.length > 0 ||
    activeHighlightedLineNumbers.length > 0;
  const scrollTimeoutRef = useRef<number | null>(null);

  const applyHighlightsAndScroll = useCallback(() => {
    if (!containerElement) {
      return;
    }
    highlightAndScroll({
      container: containerElement,
      defaultHighlighted: defaultHighlightedLineNumbers,
      activeHighlighted: activeHighlightedLineNumbers,
      scrollTimeoutRef,
    });
    setHighlightedText(getHighlightedYamlText(containerElement, 'active'));
  }, [
    activeHighlightedLineNumbers,
    containerElement,
    defaultHighlightedLineNumbers,
  ]);

  useEffect(() => {
    if (!containerElement) return;
    applyHighlightsAndScroll();

    return () => {
      if (scrollTimeoutRef.current !== null) {
        window.clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
      clearYamlHighlights(containerElement);
    };
  }, [containerElement, applyHighlightsAndScroll, props.data]);

  useCallbackWhenDomChanges(
    applyHighlightsAndScroll,
    hasHighlights && Boolean(containerElement),
    containerElement
  );

  const handleLineClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!manifestId || !onLineClick) {
      return;
    }
    const lineNumber = getLineNumberFromEvent(event);
    if (lineNumber === undefined) {
      return;
    }
    onLineClick(manifestId, lineNumber);
  };

  return (
    <Box
      ref={setContainerElement}
      onClick={handleLineClick}
      sx={{
        height: '100%',
        position: 'relative',
        '& .DsPreviewYaml-Line-root[data-highlighted-default="true"]': {
          backgroundColor: withAlpha(tokens.theme.bg.status.errorStrong, 0.2),
          boxShadow: `inset 2px 0 0 ${tokens.theme.border.status.error}`,
          cursor: 'pointer',
        },
        '& .DsPreviewYaml-Line-root[data-highlighted-active="true"]': {
          backgroundColor: withAlpha(tokens.theme.bg.status.errorStrong, 0.4),
          boxShadow: `inset 2px 0 0 ${tokens.theme.border.status.error}`,
          cursor: 'pointer',
        },
      }}
    >
      <PreviewYaml
        {...props}
        actions={
          highlightedText ? (
            <CopyToClipboard iconSize="1em" text={highlightedText} />
          ) : null
        }
      >
        {children}
      </PreviewYaml>
    </Box>
  );
};
